import { phone } from '@parsina/framework';

import {
  UNPROVISIONED, STATUS_SEVERITY, TIER_ORDER, tierRank,
} from '../constants/subscription';
import { PAGE_SIZE, SEARCH_MIN_CHARS } from '../constants/list';
import { lapseAnchor } from './format';
import { dateRangeFilter } from './demoRequestsView';

/**
 * When a clinic was registered, as epoch ms — or null when the row has no usable
 * date.
 *
 * THE REGISTRY'S TIMESTAMP IS UTC WITHOUT A ZONE MARK. `clinics.created_at` is a
 * naive column filled by `datetime.utcnow`, and the service serialises it with a
 * bare `isoformat()` («2026-10-28T09:07:43.867038»). `new Date()` reads a string
 * like that as the BROWSER's local time, so a clinic registered at 21:00 UTC would
 * sit on the wrong side of a Tehran midnight. A value with no zone designator is
 * therefore read as UTC here, which is what the column holds.
 */
export function createdMs(row) {
  const raw = row?.createdAt;
  if (!raw) return null;
  const text = String(raw);
  const ms = new Date(/(Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Narrow, sort and page the estate — a PURE function of (rows, view).
 *
 * All of it happens in the browser, and that is a property of the data rather
 * than a shortcut: the BFF's `clinics` resolver already returns every clinic,
 * because it joins the registry to ONE batched entitlement call (a lookup per
 * clinic would be thousands of round trips). Its own `search`/`status` arguments
 * then filter that same in-memory array. So asking the server to narrow costs a
 * round trip and returns a subset of what we already hold.
 *
 * Keeping it pure and out of the component is what makes the filter semantics
 * testable without rendering — the un-provisioned/tier interaction below is the
 * kind of rule that is easy to get subtly wrong and impossible to see in a
 * screenshot.
 */

/**
 * Does this row match the typed query?
 *
 * Name is matched case-insensitively; phone is matched after folding digits
 * through the kit's `phone.foldDigits`, so «۰۲۱۱۱» finds the clinic stored as
 * "02111". A search box that only accepts one digit set is a search box that
 * fails for whoever is typing on the other keyboard, which in this product is
 * everybody at some point.
 *
 * THE FOLD IS THE KIT'S, not a local table, and the difference is a digit set:
 * the table this replaced covered Persian (U+06F0) and Arabic-Indic (U+0660)
 * only, while the kit also folds FULLWIDTH U+FF10–FF19 — which some IME
 * keyboards emit, and which its Java and Python twins have always folded. A
 * clinic whose number arrived in that block was unsearchable here by any
 * spelling of it.
 *
 * BOTH SPELLINGS of the number are searched, and the second one is not
 * redundant. The registry stores E.164 now («+982112345678») while the table
 * renders the national form («02112345678») — so an operator who types what is
 * on their screen is typing a string the stored value does not contain, and the
 * list would come back empty for a clinic that is right there in it. Matching
 * the displayed form as well is what keeps "search for what you can see"
 * true; matching the stored form as well is what keeps a pasted «+98…» working.
 */
function matches(row, query) {
  const q = phone.foldDigits(query).trim().toLowerCase();
  if (!q) return true;
  const name = String(row.name || '').toLowerCase();
  // No `|| ''` on either call: `foldDigits` answers '' for a non-string, which is
  // the right reading of a row that simply has no phone.
  const stored = phone.foldDigits(row.officePhone);
  const shown = phone.foldDigits(phone.forDisplay(row.officePhone));
  return name.includes(q) || stored.includes(q) || (shown !== '' && shown.includes(q));
}

/** The value the status filter compares against — a missing row is its own state. */
const statusOf = (row) => (row.hasSubscription ? row.subscriptionStatus : UNPROVISIONED);

const SORTERS = {
  // localeCompare with the ACTIVE locale: Persian collation is not ASCII order,
  // and sorting «الف» against «ب» by code point gives an order no reader expects.
  name: (a, b, locale) => String(a.name || '').localeCompare(String(b.name || ''), locale),
  status: (a, b) => STATUS_SEVERITY.indexOf(statusOf(a)) - STATUS_SEVERITY.indexOf(statusOf(b)),
  tier: (a, b) => tierRank(a.capabilitySet) - tierRank(b.capabilitySet),
  entitled: (a, b) => Number(a.entitled) - Number(b.entitled),
  created: (a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')),
  until: (a, b) => String(lapseAnchor(a) || '').localeCompare(String(lapseAnchor(b) || '')),
};

/**
 * Columns whose value can be ABSENT, and how to tell.
 *
 * Rows with no value sort LAST in BOTH directions, so they are partitioned out
 * before the comparator rather than handled inside it. Doing it inside does not
 * work: the comparator's result is multiplied by the direction, so a `return 1`
 * for "a is missing" becomes `-1` under `desc` and the empties jump to the top —
 * which is exactly the bug this comment replaces.
 *
 * It matters because "no end date" is not the earliest date, it is the absence of
 * one. Sorted to the top of an ascending list, the clinics that need nothing would
 * sit where the urgent ones belong.
 */
const MISSING = {
  until: (r) => !lapseAnchor(r),
  tier: (r) => tierRank(r.capabilitySet) < 0,
};

export const SORT_KEYS = Object.keys(SORTERS);

/**
 * @param rows  every clinic, as the BFF returned it
 * @param view  { q, status, tier, entitled, from, to, sort, dir, page } from the URL
 * @param opts  { locale } for collation
 * @returns { rows, matching, total, filtered, pageCount, page, unprovisioned }
 */
export function clinicsView(rows, view, { locale = 'fa' } = {}) {
  const all = Array.isArray(rows) ? rows : [];
  const unprovisioned = all.filter((r) => !r.hasSubscription).length;

  const q = String(view.q || '');
  // One character matches most of the estate and narrows nothing, so it is
  // treated as "not searching yet" rather than as a filter. Zero characters is a
  // different thing — that is cleared.
  const searching = q.trim().length >= SEARCH_MIN_CHARS;

  let out = all;
  if (view.status && view.status !== 'ALL') out = out.filter((r) => statusOf(r) === view.status);
  if (view.tier && view.tier !== 'ALL') {
    // An un-provisioned clinic has NO tier, so it drops out of every tier filter
    // rather than being swept into one: «بدون اشتراک» is its own state, and the
    // status filter above it is where that clinic is found. Sweeping it into a
    // tier would let an operator filter by طلایی and be shown clinics nobody
    // ever provisioned.
    out = out.filter((r) => r.hasSubscription && r.capabilitySet === view.tier);
  }
  if (view.entitled === '1') out = out.filter((r) => r.entitled);
  if (view.entitled === '0') out = out.filter((r) => !r.entitled);
  if (searching) out = out.filter((r) => matches(r, q));

  // The registration window — two Tehran calendar days from the pickers, both
  // ends inclusive (`dateRangeFilter` covers the whole of `to` and swaps a
  // reversed range). A clinic with no readable date is OUT of any window: it
  // cannot be claimed as registered inside one.
  const range = dateRangeFilter(view.from, view.to);
  const sinceMs = range.createdFromMs == null ? null : Number(range.createdFromMs);
  const untilMs = range.createdToMs == null ? null : Number(range.createdToMs);
  const windowed = sinceMs != null || untilMs != null;
  if (windowed) {
    out = out.filter((r) => {
      const ms = createdMs(r);
      return ms != null && (sinceMs == null || ms >= sinceMs) && (untilMs == null || ms <= untilMs);
    });
  }

  const sortKey = SORTERS[view.sort] ? view.sort : 'created';
  const sorter = SORTERS[sortKey];
  const dir = view.dir === 'asc' ? 1 : -1;
  const isMissing = MISSING[sortKey];

  // Copy before sorting: the caller's array is React state, and sorting it in
  // place mutates the render's own input.
  const present = isMissing ? out.filter((r) => !isMissing(r)) : [...out];
  const absent = isMissing ? out.filter((r) => isMissing(r)) : [];
  out = [
    ...present.sort((a, b) => sorter(a, b, locale) * dir),
    ...absent,
  ];

  const filtered = out.length;
  const pageCount = Math.max(1, Math.ceil(filtered / PAGE_SIZE));
  // A filter that shrinks the list below the current page would otherwise leave
  // the operator on an empty page 4 of 1, which reads as "no results".
  const page = Math.min(Math.max(1, Number(view.page) || 1), pageCount);
  const start = (page - 1) * PAGE_SIZE;

  return {
    rows: out.slice(start, start + PAGE_SIZE),
    // Every row the filters left, in the on-screen order and NOT paged — what
    // «خروجی اکسل» writes. Exporting `rows` would hand over one page of forty.
    matching: out,
    total: all.length,
    filtered,
    pageCount,
    page,
    unprovisioned,
    // Whether ANY narrowing is in force — drives «حذف فیلترها» and tells the
    // empty state whether to say "nothing here" or "nothing matches".
    isFiltered: searching
      || windowed
      || (!!view.status && view.status !== 'ALL')
      || (!!view.tier && view.tier !== 'ALL')
      || view.entitled === '0' || view.entitled === '1',
  };
}

/** The tier filter's options, ALL first. */
export const TIER_FILTERS = ['ALL', ...TIER_ORDER];

export default clinicsView;
